import { WorldPoint, Direction, Route, route_from_json } from './grid';
import { AtlasCoord, JsonDictionaryWrapper } from './utils';
import { Building } from './building';
import { Shot, Gun, FireControl, GunType } from './gun';

export interface RouteFinder {
    find_route(start: WorldPoint, finish: WorldPoint): Route | null;
}

export interface BuildingFinder {
    find_building(wp: WorldPoint): Building | null;
    find_live_building_near(wp: WorldPoint, r: number): Building | null;
    get_hq(): Building;
}

export type EnemyVisualState = {
    proportion: number 
    source: WorldPoint
    destination: WorldPoint
}

export type EnemyProperties = {
    // how long before it can fire again
    cooldown_ticks: number 

    // only check for targets every n ticks (to throttle it a bit)
    target_seek_cooldown_ticks: number 

    // speed of movement
    ticks_across_cell: number

    // enemy's hp
    hp: number

    // enemy image
    sprite: AtlasCoord
}

export class Enemy {
    public hp: number

    private _gun: Gun
    private _route: Route | null = null
    private _step_ticks: number = 0
    private _step_index: number = 0
    private _last_pt: WorldPoint = new WorldPoint(0, 0)
    private _properties: EnemyProperties
    private _cooldown_ticks: number = 0
    private _cooling_down_from_shot: boolean = false

    private toJSON(): any {
        return {
            hp: this.hp,
            gun: this._gun,
            route: this._route,
            step_ticks: this._step_ticks,
            step_index: this._step_index,
            last_pt: this._last_pt,
            cooldown_ticks: this._cooldown_ticks,
            cooling_down_from_shot: this._cooling_down_from_shot,
        }
    }

    public static create_json(obj: JsonDictionaryWrapper): Enemy {
        let p = WorldPoint.create_json(obj)
        let e = new Enemy(p)

        e.hp = obj.get_number("hp")
        e._gun = Gun.create_json(obj.get_object("gun"))
        if (obj.is_null("route")) {
            e._route = route_from_json(obj.get_object("route"))
        }
        e._step_ticks = obj.get_number("step_ticks")
        e._step_index = obj.get_number("step_index")
        e._last_pt = WorldPoint.create_json(obj.get_object("last_pt"))
        e._cooldown_ticks = obj.get_number("cooldown_ticks")
        e._cooling_down_from_shot = obj.get_boolean("cooling_down_from_shot")

        return e
    }

    constructor(p: WorldPoint) {
        this._properties = {
            cooldown_ticks: 120,
            target_seek_cooldown_ticks: 20,
            sprite: { i: 4, j: 0 },
            ticks_across_cell: 60,
            hp: 20,
        }

        this._gun = new Gun(GunType.Basic)
        this._last_pt = p
        this.hp = this.max_hp
    }

    // tiles considered occupied by this enemy
    public get positions(): WorldPoint[] {
        const vs = this.visual_state
        return [ vs.source, vs.destination ]
    }

    public get hp_proportion(): number {
        return this.hp / this.max_hp
    }

    public get visual_state(): EnemyVisualState {
        if (this._route == null) {
            return {
                proportion: 0,
                source: this._last_pt,
                destination: this._last_pt,
            }
        } else {
            return {
                proportion: this._step_ticks / this._properties.ticks_across_cell,
                source: this._last_pt,
                destination: this._last_pt.translated(this._route.directions[this._step_index]),
            }
        }
    }

    public get max_hp(): number {
        return this._properties.hp
    }

    public sprite(): AtlasCoord {
        return this._properties.sprite
    }

    public requires_reroute(): void {
        this._route = null
    }

    public tick(bf: BuildingFinder, rf: RouteFinder, fc: FireControl): void {
        this._gun.tick()
        
        // route
        if (this._route == null) {
            const rt = rf.find_route(this.visual_state.source, bf.get_hq().position) 
            if (rt != null) {
                this._route = rt
                this._step_ticks = 0
                this._step_index = 0
                this._last_pt = rt.source
            }
        }

        // move
        if (this._route != null && !this._cooling_down_from_shot) {
            this._step_ticks += 1
            if (this._step_ticks == this._properties.ticks_across_cell) {
                this._step_ticks = 0

                this._last_pt = this._last_pt.translated(this._route.directions[this._step_index])
                this._step_index += 1
                if (this._step_index == this._route.directions.length) {
                    this._last_pt = this._route.destination
                    this._route = null
                }
            }
        }

        // shoot
        if (this._gun.can_fire) {
            // find a target
            let b = bf.find_live_building_near(this._last_pt, this._gun.range)
            if (b != null) {
                this._cooldown_ticks = this._properties.cooldown_ticks
                this._cooling_down_from_shot = true
                fc.fire_shot(this._gun.fire(this._last_pt, b.position))
            }

            this._cooling_down_from_shot = false
            this._cooldown_ticks = this._properties.target_seek_cooldown_ticks
        }
    }
}
